<script>
import SiparislerService from "@/services/SiparislerService";
import { ref } from "@vue/composition-api";
import KullanicilarService from "@/services/KullanicilarService";
import AddressService from "@/services/AddressService";
import UrunListFragment from "@/components/Modules/Urunler/UrunListFragment.vue";
import SiparisStatusChip from "@/components/Modules/Siparisler/SiparisStatusChip.vue";
import SelectAddressModal from "@/components/Modules/Addresses/SelectAddressModal.vue";

export default {
  name: "SiparisDetayModal",
  components: { SelectAddressModal, SiparisStatusChip, UrunListFragment },

  props: {
    value: {
      required: true,
      type: Boolean
    },
    orderId: {
      type: Number
    }
  },

  setup() {
    const orderDetailLoading = ref(false);
    const orderDetail = ref(null);
    const userDetail = ref(null);
    const addressDetail = ref(null);
    const shippingDetail = ref(null);
    const selectAddressModal = ref(false);
    const changeAddressLoading = ref(false);

    return {
      orderDetail,
      orderDetailLoading,
      userDetail,
      addressDetail,
      shippingDetail,
      selectAddressModal,
      changeAddressLoading
    };
  },

  methods: {
    getOrderDetail(orderId) {
      this.orderDetailLoading = true;
      SiparislerService.siparisDetay(orderId).then(({ status, data }) => {
        const promiseAll = [];
        if (status === 200) {
          this.orderDetail = data;

          const { id, user, address, shipping } = data;

          if (address) {
            promiseAll.push(this.getAddressDetail(address).then(({ data }) => this.addressDetail = data));
          }
          if (shipping) {
            promiseAll.push(this.getShippingDetail(id).then(({ data }) => this.shippingDetail = data));
          }
          promiseAll.push(this.getUserDetail(user).then(({ data }) => this.userDetail = data));
        }

        Promise.all(promiseAll).then(() => {
          this.orderDetailLoading = false;
        });
      }).catch(() => {
        this.orderDetailLoading = false;
      });
    },

    getUserDetail(kullaniciId) {
      return KullanicilarService.kullaniciDetay(kullaniciId);
    },

    getAddressDetail(addressId) {
      return AddressService.addressDetail(addressId);
    },

    getShippingDetail(shippingId) {
      return SiparislerService.siparisKargoDetay(shippingId);
    },

    changeOrderAddress(address) {
      this.changeAddressLoading = true;
      SiparislerService.changeOrderAddress({
        orderId: this.orderId, addressId: address.id
      }).then(({ status, message }) => {
        if (status === 200) {
          this.addressDetail = address;
          this.$snotify.success("Sipariş adresi değiştirildi", this.$t("PANEL.NOTIFY.SUCCESS"));
        } else {
          this.$snotify.error(message, this.$t("PANEL.NOTIFY.ERROR"));
        }
        this.changeAddressLoading = false;
        this.selectAddressModal = false;
      }).catch(() => {
        this.changeAddressLoading = false;
        this.selectAddressModal = false;
      });
    }
  },

  watch: {
    orderId() {
      this.getOrderDetail(this.orderId);
    }
  }
};
</script>

<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1200px">
    <v-card color="white" class="pb-1">
      <v-card-title class="align-center">
        <div>
          Sipariş Bilgileri
          <span v-if="orderDetail" class="text-caption">
            ({{ orderDetail.kod }})
          </span>
        </div>
        <v-spacer />
        <div class="d-flex align-center">
          <div class="mr-2">
            <v-chip v-if="orderDetail" x-small>
              Son Güncelleme:
              {{ $dayjs(orderDetail.update).format("DD/MM/YYYY HH:mm") }}
            </v-chip>
          </div>
          <v-btn icon @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider />
      <v-card-text class="pb-2">
        <div v-if="orderDetailLoading" class="d-flex flex-column align-center py-10">
          <v-progress-circular
            size="100"
            indeterminate
            color="primary"
            class="mb-2"
          ></v-progress-circular>
          <div class="text-body-1 font-weight-medium">
            Sipariş Bilgileri Yükleniyor
          </div>
        </div>
        <v-row v-else-if="orderDetail">
          <v-col cols="12" lg="6">
            <h4 class="black--text">
              <v-icon size="1.1rem">mdi-play</v-icon>
              Ürün Bilgileri
            </h4>
            <div class="px-5">
              <urun-list-fragment :value="orderDetail.products" />
            </div>
            <h4 class="black--text mb-3">
              <v-icon size="1.1rem">mdi-play</v-icon>
              Sipariş Bilgileri
            </h4>
            <div class="px-5">
              <v-row>
                <v-col cols="3" class="py-2">
                  <div class="font-weight-medium black--text">Sipariş Durumu:</div>
                </v-col>
                <v-col cols="9" class="py-2">
                  <siparis-status-chip :value="orderDetail.status" />
                </v-col>
                <v-col cols="3" class="py-2">
                  <div class="font-weight-medium black--text">Sipariş Kodu:</div>
                </v-col>
                <v-col cols="9" class="py-2">
                  {{ orderDetail.kod }}
                </v-col>
                <v-col cols="3" class="py-2">
                  <div class="font-weight-medium black--text">Oluşturulma Tarihi:</div>
                </v-col>
                <v-col cols="9" class="py-2">
                  {{ $dayjs(orderDetail.date).format("DD/MM/YYYY HH:mm") }}
                </v-col>
                <v-col cols="3" class="py-2">
                  <div class="font-weight-medium black--text">Sipariş Tarihi:</div>
                </v-col>
                <v-col cols="9" class="py-2">
                  {{ $dayjs(orderDetail.orderdate).format("DD/MM/YYYY HH:mm") }}
                </v-col>
                <v-col cols="3" class="py-2">
                  <div class="font-weight-medium black--text">Teslim Tarihi:</div>
                </v-col>
                <v-col cols="9" class="py-2">
                  {{ orderDetail.deliverydate ? $dayjs(orderDetail.deliverydate).format("DD/MM/YYYY HH:mm") : "-" }}
                </v-col>
                <v-col cols="3" class="py-2">
                  <div class="font-weight-medium black--text">Sipariş Tutarı:</div>
                </v-col>
                <v-col cols="9" class="py-2">
                  {{ $formatFiyat(orderDetail.amount) }}
                </v-col>
              </v-row>
            </div>
          </v-col>
          <v-col cols="12" lg="6">
            <h4 class="black--text mb-2">
              <v-icon size="1.1rem">mdi-play</v-icon>
              Kullanıcı Bilgileri
            </h4>
            <div class="px-5">
              <v-row>
                <v-col class="font-weight-medium black--text py-1 pl-4" cols="4">Kullanıcı:</v-col>
                <v-col cols="8" class="py-1">{{ userDetail.name }}</v-col>
                <v-col class="font-weight-medium black--text py-1 pl-4" cols="4">E-posta:</v-col>
                <v-col cols="8" class="py-1">{{ userDetail.email }}</v-col>
                <v-col class="font-weight-medium black--text py-1 pl-4" cols="4">Telefon:</v-col>
                <v-col cols="8" class="py-1">{{ userDetail.telephone }}</v-col>
                <v-col class="font-weight-medium black--text py-1 pl-4" cols="4">Cinsiyet:</v-col>
                <v-col cols="8" class="py-1">{{ userDetail.gender }}</v-col>
              </v-row>
            </div>
            <div class="my-5"></div>
            <div>
              <div class="d-flex justify-space-between align-center mb-2">
                <h4 class="black--text mb-2">
                  <v-icon size="1.1rem">mdi-play</v-icon>
                  Adres Bilgileri
                </h4>
                <div>
                  <v-btn icon @click="selectAddressModal = true">
                    <v-icon size="1.2rem">mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </div>
              <div v-if="addressDetail">
                <v-card
                  class="mb-2"
                  elevation="0"
                >
                  <v-card-text class="py-0">
                    <span class="text-body-1 mb-1 font-weight-medium d-block">{{ addressDetail.name }}</span>
                    <div class="text-body-2 mb-1">Ad Soyad: {{ addressDetail.namesurname }}</div>
                    <span class="text-grey-8">Mahalle:</span>
                    {{ addressDetail.neighbourhood }}
                    <br />
                    <span class="text-grey-8">Adres:</span>
                    {{ addressDetail.address }}
                    <span v-show="addressDetail.postcode">- {{ addressDetail.postcode }},</span>
                    {{ addressDetail.district }}/{{ addressDetail.city }}
                    <br />
                    <span class="text-grey-8">Gsm:</span>
                    {{ addressDetail.telephone }}
                    <br />
                    <br />
                    <span class="text-grey-8">Fatura Türü:</span>
                    {{ addressDetail.type === 1 ? "Bireysel" : "Kurumsal" }}
                    <br />
                    <span v-if="addressDetail.type === 1">
                      <span class="text-grey-8">T.C.:</span>
                      {{ addressDetail.citizenshipnumber }}
                    </span>
                    <span v-else>
                      <span class="text-grey-8">Firma Adı:</span>
                      {{ addressDetail.companyname }}
                      <br />
                      <span class="text-grey-8">Vergi Numarası:</span>
                      {{ addressDetail.taxnumber }}
                      <br />
                      <span class="text-grey-8">Vergi Dairesi:</span>
                      {{ addressDetail.taxoffice }}
                    </span>
                  </v-card-text>
                </v-card>
              </div>
              <div v-else class="text-center grey lighten-4 py-2">
                <p class="mb-0">Adres Seçimi Yapılmadı</p>
              </div>
            </div>
            <div class="my-5"></div>
            <div>
              <h4 class="black--text mb-2">
                <v-icon size="1.1rem">mdi-play</v-icon>
                Kargo Bilgileri
              </h4>
              <div v-if="shippingDetail" class="px-5">
                <div>Kargo: {{ shippingDetail.name }}</div>
                <div>Kargo Takip No: {{ shippingDetail.number }}</div>
                <div>Not: {{ shippingDetail.note }}</div>
              </div>
              <div v-else class="text-center grey lighten-4 py-2">
                <p class="mb-0">Kargo Bilgisi Girilmedi</p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn small color="red" dark @click="$emit('input', false)">
          <v-icon size="1.25rem" class="mr-1">mdi-close</v-icon>
          Kapat
        </v-btn>
      </v-card-actions>
    </v-card>
    <select-address-modal v-if="selectAddressModal" v-model="selectAddressModal" :user-id="orderDetail.user"
                          @select="changeOrderAddress" />
  </v-dialog>
</template>

<style scoped>

</style>